import { useEffect, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { TeambinderAiApiService } from '@src/api';
import { ApiError } from '@src/api/core/ApiError';

const STORAGE_KEY = 'pendingKeywordIds';
const CHECK_INTERVAL = 10000; // 10초

interface PendingKeyword {
  id: string;
  name: string;
}

interface UseKeywordDefinitionCheckerProps {
  onDefinitionFound?: (keywordId: string, keywordName: string) => void;
  enableChecking?: boolean; // 체크 기능 활성화 여부
}

export const useKeywordDefinitionChecker = ({
  onDefinitionFound,
  enableChecking = false, // 기본적으로 비활성화
}: UseKeywordDefinitionCheckerProps = {}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkedKeywordsRef = useRef<Set<string>>(new Set());

  // 로컬 스토리지에 키워드 추가 (이 기능은 항상 사용 가능)
  const addKeywordToCheck = useCallback(
    (keywordId: string, keywordName: string) => {
      const existingKeywords = localStorage.getItem(STORAGE_KEY);
      const keywords: PendingKeyword[] = existingKeywords
        ? JSON.parse(existingKeywords)
        : [];
      const exists = keywords.some((k) => k.id === keywordId);
      if (!exists) {
        keywords.push({ id: keywordId, name: keywordName });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(keywords));
      }
    },
    [],
  );

  // 로컬 스토리지에서 키워드 제거
  const removeKeywordFromStorage = useCallback((keywordId: string) => {
    const existingKeywords = localStorage.getItem(STORAGE_KEY);
    if (existingKeywords) {
      const keywords: PendingKeyword[] = JSON.parse(existingKeywords);
      const filteredKeywords = keywords.filter((k) => k.id !== keywordId);
      if (filteredKeywords.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredKeywords));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // 키워드 정의 확인
  // 반환값: 'found' (정의 있음), 'pending' (아직 생성 중), 'not_found' (키워드 자체가 없음)
  const checkKeywordDefinition = useCallback(
    async (keywordId: string): Promise<'found' | 'pending' | 'not_found'> => {
      try {
        const response = await TeambinderAiApiService.getKeywordContext({
          keywordId,
        });

        // 키워드 정의가 있는지 확인 (data가 있고 비어있지 않은 경우)
        if (response.data && response.data.trim() !== '') {
          return 'found';
        }
        return 'pending';
      } catch (error) {
        if (error instanceof ApiError && error.status === 400) {
          const body = error.body as { code?: string } | undefined;
          // 서버에서 code "404"로 키워드 미생성(not found)을 구분
          if (body?.code === '404') {
            return 'pending';
          }
          // 그 외 400 에러 (잘못된 keyword-id 등)
          return 'not_found';
        }
        console.error(
          `Failed to check keyword definition for ${keywordId}:`,
          error,
        );
        return 'pending';
      }
    },
    [],
  );

  // 모든 대기 중인 키워드 확인
  const checkPendingKeywords = useCallback(async () => {
    if (!enableChecking) return; // 체크 기능이 비활성화되어 있으면 실행하지 않음

    const storedKeywords = localStorage.getItem(STORAGE_KEY);
    if (!storedKeywords) return;

    const keywords: PendingKeyword[] = JSON.parse(storedKeywords);
    if (keywords.length === 0) return;

    for (const keyword of keywords) {
      // 이미 확인한 키워드는 건너뛰기
      if (checkedKeywordsRef.current.has(keyword.id)) {
        continue;
      }

      const result = await checkKeywordDefinition(keyword.id);

      // 존재하지 않는 키워드는 폴링 목록에서 제거
      if (result === 'not_found') {
        removeKeywordFromStorage(keyword.id);
        continue;
      }

      if (result === 'found') {
        checkedKeywordsRef.current.add(keyword.id);
        removeKeywordFromStorage(keyword.id);

        Modal.info({
          title: (
            <>
              <b>{keyword.name}</b>에 대한 키워드 정의가 생성되었습니다.
            </>
          ),
          content: (
            <div>
              <ul style={{ paddingLeft: '10px' }}>
                <li>올바르게 키워드가 설정되었는지 꼭 확인해주세요.</li>
                <li>키워드 설정이 잘못되었다면 올바르게 수정해주세요</li>
              </ul>
            </div>
          ),
          okText: '확인',
          onOk: () => {
            if (onDefinitionFound) {
              onDefinitionFound(keyword.id, keyword.name);
            }
          },
        });
      }
    }
  }, [
    checkKeywordDefinition,
    onDefinitionFound,
    removeKeywordFromStorage,
    enableChecking,
  ]);

  // 주기적으로 확인 시작
  const startChecking = useCallback(() => {
    if (!enableChecking) return; // 체크 기능이 비활성화되어 있으면 실행하지 않음
    if (intervalRef.current) return;

    // 즉시 한 번 확인
    checkPendingKeywords();

    // 주기적으로 확인
    intervalRef.current = setInterval(() => {
      checkPendingKeywords();
    }, CHECK_INTERVAL);
  }, [checkPendingKeywords, enableChecking]);

  // 확인 중지
  const stopChecking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 컴포넌트 마운트 시 확인 시작, 언마운트 시 중지
  useEffect(() => {
    if (enableChecking) {
      startChecking();
    }

    return () => {
      stopChecking();
    };
  }, [startChecking, stopChecking, enableChecking]);

  return {
    addKeywordToCheck,
    removeKeywordFromStorage,
    startChecking,
    stopChecking,
  };
};

export default useKeywordDefinitionChecker;
